import type { CallJourney } from "@starsach/shared-types";
import { minutes } from "./trace";

/**
 * requirements.md 8.5: the flag strip - what went wrong on this contact,
 * in a sentence, from a rule set over the assembled journey. The rules
 * are listed in design.md "Journeys module"; a new rule is a line here
 * and a line there.
 */
export interface Flag {
  severity: "critical" | "warning" | "info";
  text: string;
}

const RANK = { critical: 0, warning: 1, info: 2 };
const NEG = (n: number | undefined) => n !== undefined && n < 3.5;

export function flagsFor(j: CallJourney): Flag[] {
  const flags: Flag[] = [];
  const legs = j.legs ?? [];
  const steps = j.timeline?.status === "ok" ? j.timeline.steps : [];

  for (const s of steps) if (s.error) flags.push({ severity: "critical", text: `Flow error at ${s.identifier ?? s.moduleType}` });

  // Retries: the same block entered again and again with nothing else between - the menu re-prompting, the capture asking once more.
  let run = 1;
  for (let i = 1; i <= steps.length; i++) {
    const same = i < steps.length && steps[i]!.identifier !== undefined && steps[i]!.identifier === steps[i - 1]!.identifier && steps[i]!.flowName === steps[i - 1]!.flowName;
    if (same) run += 1;
    else {
      if (run >= 3) flags.push({ severity: "warning", text: `Retried ${run} times at ${steps[i - 1]!.identifier}` });
      run = 1;
    }
  }

  if (j.lambdas?.status === "ok") {
    for (const inv of j.lambdas.invocations) {
      if (inv.logLines.some((l) => /\b(error|task timed out|unhandled)\b/i.test(l))) flags.push({ severity: "critical", text: `Lambda error: ${inv.functionName}` });
    }
  }

  const fallbacks = j.bot?.status === "ok" ? j.bot.turns.filter((t) => t.fallback).length : 0;
  if (fallbacks) flags.push({ severity: "warning", text: `Bot fell back ${fallbacks === 1 ? "once" : `${fallbacks} times`}` });

  for (const l of legs) {
    const enq = l.enqueuedAt ? Date.parse(l.enqueuedAt) : NaN;
    const agentAt = l.connectedToAgentAt ? Date.parse(l.connectedToAgentAt) : NaN;
    const end = l.disconnectTimestamp ? Date.parse(l.disconnectTimestamp) : NaN;
    if (Number.isFinite(enq)) {
      if (Number.isFinite(agentAt)) {
        if (agentAt - enq >= 120_000) flags.push({ severity: "warning", text: `Waited ${minutes(agentAt - enq)} in queue` });
      } else if (Number.isFinite(end) && !l.agentId) {
        flags.push({ severity: "critical", text: `Hung up in queue after ${minutes(end - enq)}` });
      }
    }
    if (l.agentHoldMs && l.agentHoldMs >= 120_000) flags.push({ severity: "info", text: `On hold ${minutes(l.agentHoldMs)}` });
    for (const side of ["agent", "customer"] as const) {
      const q = l.audioQuality?.[side];
      if (q && (NEG(q.score) || q.issues.length)) flags.push({ severity: "warning", text: `Poor audio on the ${side} side${q.issues.length ? `: ${q.issues.map(issue).join(", ")}` : ""}` });
    }
    if (l.potentialDisconnectIssue) flags.push({ severity: "warning", text: `Disconnect issue: ${issue(l.potentialDisconnectIssue)}` });
  }

  if (legs.length > 1) flags.push({ severity: "info", text: `Transferred ${legs.length - 1 === 1 ? "once" : `${legs.length - 1} times`}` });

  return flags.sort((a, b) => RANK[a.severity] - RANK[b.severity]);
}

/** Connect's constants read as words: HighPacketLoss -> high packet loss, AGENT_CONNECTIVITY_ISSUE -> agent connectivity issue. */
export function issue(code: string): string {
  return code.includes("_") ? code.toLowerCase().replace(/_/g, " ") : code.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
}
